import { __ } from '../../utils';

export type Crumb = { href?: string; label: string };

const HOME: Crumb = { href: '/', label: __('Domov') };

/**
 * `/administracia` presmeruje na `/`, takže tento článok cesty je zámerne bez
 * `href` - vykreslí sa ako tichý text. Nie je to odkaz nikam, je to úroveň,
 * ktorá dáva každej obrazovke administrácie ten istý základ.
 */
const ADMIN: Crumb = { label: __('Administrácia') };

/**
 * Sekcie administrácie. `label` je ten istý, ktorý nesie navigácia
 * ([navigationLinks](../Navigation/navigationLinks.tsx)) - cesta je odraz
 * navigačného stromu, takže sa nesmú rozchádzať.
 */
export const ADMIN_SECTION = {
  reports: { label: __('Správa podnetov'), href: '/administracia/podnety' },
  benefits: { label: __('Správa výhod'), href: '/administracia/vyhody' },
  votings: { label: __('Správa hlasovaní'), href: '/administracia/hlasovania' },
  projects: { label: __('Správa projektov'), href: '/administracia/projekty' },
  newsCategories: {
    label: __('Správa noviniek'),
    href: '/administracia/kategorie-novinky',
  },
  aiDecisions: { label: __('AI rozhodnutia'), href: '/administracia/ai' },
} satisfies Record<string, Crumb>;

/**
 * Kategórie podnetov nie sú sekcia navigácie, ale podstránka správy podnetov -
 * vchádza sa do nich tlačidlom „Spravovať kategórie" v hlavičke zoznamu.
 * Cesta to tak aj hovorí a šípka späť z nich vedie na zoznam podnetov.
 */
const REPORT_CATEGORIES: Crumb = {
  label: __('Kategórie podnetov'),
  href: '/administracia/kategorie-podnetov',
};

/** Cesta v administrácii je vždy `Domov > Administrácia > …`. */
export const adminCrumbs = (...tail: Crumb[]): Crumb[] => [
  HOME,
  ADMIN,
  ...tail,
];

/**
 * Hotové cesty pre `crumbs` na `RouteMetadata`. Sú to **konštanty**, nie
 * volanie `adminCrumbs()` v JSX: `crumbs` je závislosť efektu, ktorý cestu
 * ukladá, a nové pole pri každom renderi by ten efekt spúšťalo dokola -
 * vrátane prepisovania cesty, ktorú si detailová stránka dopĺňa sama.
 *
 * Posledný článok kreslí `Breadcrumbs` ako tichú pilulku, takže detail má
 * vlastný list; bez neho by sa ako „tu som" označila sekcia, v ktorej len
 * ležím.
 */
export const ADMIN_CRUMBS = {
  reports: adminCrumbs(ADMIN_SECTION.reports),
  reportDetail: adminCrumbs(ADMIN_SECTION.reports, {
    label: __('Detail podnetu'),
  }),
  benefits: adminCrumbs(ADMIN_SECTION.benefits),
  benefitDetail: adminCrumbs(ADMIN_SECTION.benefits, {
    label: __('Detail výhody'),
  }),
  votings: adminCrumbs(ADMIN_SECTION.votings),
  votingDetail: adminCrumbs(ADMIN_SECTION.votings, {
    label: __('Detail hlasovania'),
  }),
  projects: adminCrumbs(ADMIN_SECTION.projects),
  projectDetail: adminCrumbs(ADMIN_SECTION.projects, {
    label: __('Detail projektu'),
  }),
  reportCategories: adminCrumbs(ADMIN_SECTION.reports, REPORT_CATEGORIES),
  reportCategoryDetail: adminCrumbs(ADMIN_SECTION.reports, REPORT_CATEGORIES, {
    label: __('Detail kategórie'),
  }),
  newsCategories: adminCrumbs(ADMIN_SECTION.newsCategories),
  aiDecisions: adminCrumbs(ADMIN_SECTION.aiDecisions),
  newsCategoryDetail: adminCrumbs(ADMIN_SECTION.newsCategories, {
    label: __('Detail kategórie'),
  }),
};
